import {
  type LessonItem,
  type StudentGrade,
  addDays,
  canJoinLesson,
  gradeLevel,
  termFor,
  timeOf,
  todayIso,
  useAnnouncements,
  useAuth,
  useEvents,
  useNotifications,
  useStudentRecord,
  useTerms,
} from '@shkola/api';
import { type ToneName, subjectLook, tones } from '@shkola/design';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, type FilledIcon, SunHorizonIcon } from '../../design/icons';
import { useMinute } from '../../lib/now';
import { useAllMemberIds } from '../../pages/NotificationsPage';
import {
  AlarmArt,
  type ArtProps,
  DoneArt,
  ExamArt,
  FlagArt,
  MegaphoneArt,
  PlaneArt,
  ReworkArt,
} from './Art';
import { forStudent } from './GlassDay';
import {
  LEVEL_TONES,
  type StudentContext,
  type TodoGroup,
  type TodoItem,
  type TodoLink,
  dayLong,
  dayMonthLong,
  dayMonthShort,
  dayNow,
  metaOf,
  useShownDay,
  useTodo,
  weekdayKey,
} from './model';

/*
 * What the blocks of the home are made of (the composition agreed on 27.10, docs/ux-home-student.md §13),
 * apart from how each look draws them: «Скло», «Скло 2», «Свіжість», «Форми» and «Форми 2» read the same data.
 */

/** A card's pastel: its tone deeper at the top into light, a glow behind the picture. */
export const ground = (tone: ToneName) => {
  const t = tones[tone];
  const n = Number.parseInt(t.icon.slice(1), 16);
  const rgb = `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
  return [
    'radial-gradient(70% 45% at 80% 20%, rgb(255 255 255 / 0.5) 0%, rgb(255 255 255 / 0) 70%)',
    `linear-gradient(180deg, rgb(${rgb} / 0.18) 0%, rgb(${rgb} / 0) 62%)`,
    `linear-gradient(180deg, ${t.from} 0%, ${t.to} 100%)`,
  ].join(', ');
};

// —— the greeting: what goes on now and what is next ——

export interface NowPart {
  title: string;
  /** under the title: the place of a lesson */
  sub?: string;
  /** on the right: how long still */
  aside?: string;
  /** 0…1 of a lesson or a break gone by */
  progress?: number;
  /** minutes still to go (a lesson, a break, before the first one) */
  left?: number;
  /** the holidays: their days still to go, today counted */
  days?: number;
  tone: ToneName;
  live: boolean;
  icon?: FilledIcon;
}

export interface NextPart {
  label: string;
  lesson: LessonItem;
  /** under it: the lessons of that day, its tests */
  extra?: string;
}

/** Where the student's day stands for the greeting's card (UX §5–6): now, and the lesson that is next. */
export function useNow(ctx: StudentContext) {
  const { t } = useTranslation();
  const now = useMinute();
  const today = todayIso();
  const day = useShownDay(ctx);
  const events = useEvents(ctx.schoolId, today);
  const status = dayNow(day.todays, now);
  const minutes = (to: number) => Math.max(1, Math.ceil((to - now) / 60_000));
  const left = (to: number) => t('studentHome.glass.now.left', { min: minutes(to) });
  const holiday = (events.data ?? [])
    .filter(forStudent(ctx))
    .find((e) => e.kind === 'holiday' && e.startsOn <= today && today <= e.endsOn);

  let current: NowPart;
  let next: NextPart | undefined;
  let nothing: string | undefined;
  let join: LessonItem | undefined;

  if (status.live) {
    const start = Date.parse(status.live.startsAt);
    const end = Date.parse(status.live.endsAt);
    current = {
      title: status.live.subject,
      sub: metaOf(t, status.live),
      aside: left(end),
      left: minutes(end),
      progress: (now - start) / (end - start),
      tone: subjectLook(status.live.subject).tone,
      live: true,
    };
    if (status.next) next = { label: t('studentHome.glass.now.next'), lesson: status.next };
    else nothing = t('studentHome.glass.now.last');
    if (canJoinLesson(status.live, today)) join = status.live;
  } else if (status.inBreak && status.next) {
    const end = Date.parse(status.next.startsAt);
    const from = status.held.filter((l) => Date.parse(l.endsAt) <= now).at(-1);
    const start = from ? Date.parse(from.endsAt) : now;
    current = {
      title: t('studentHome.glass.now.break'),
      aside: left(end),
      left: minutes(end),
      progress: end > start ? (now - start) / (end - start) : 0,
      tone: 'teal',
      live: true,
    };
    next = { label: t('studentHome.glass.now.next'), lesson: status.next };
    if (canJoinLesson(status.next, today)) join = status.next;
  } else if (status.before && status.first) {
    const wait = Math.ceil((Date.parse(status.first.startsAt) - now) / 60_000);
    current = {
      title: t('studentHome.glass.now.beforeLessons'),
      aside: wait <= 90 ? left(Date.parse(status.first.startsAt)) : undefined,
      left: wait,
      tone: 'teal',
      live: wait <= 90,
    };
    next = {
      label: t('studentHome.glass.now.first'),
      lesson: status.first,
      extra: t('studentHome.glass.now.lessons', {
        lessons: t('student.lessonsCount', { count: status.held.length }),
        from: timeOf(status.first.startsAt),
        to: timeOf((status.last as LessonItem).endsAt),
      }),
    };
    if (canJoinLesson(status.first, today) && wait <= 15) join = status.first;
  } else {
    current = status.over
      ? {
          title: t('studentHome.glass.now.over'),
          aside: timeOf((status.last as LessonItem).endsAt),
          tone: 'green',
          live: false,
          icon: CheckCircleIcon,
        }
      : {
          title: holiday?.title ?? t('studentHome.glass.now.weekend'),
          aside: holiday
            ? t('studentHome.glass.now.until', { date: dayMonthLong(holiday.endsOn) })
            : undefined,
          days: holiday
            ? Math.round((Date.parse(holiday.endsOn) - Date.parse(today)) / 86_400_000) + 1
            : undefined,
          tone: 'green',
          live: false,
          icon: SunHorizonIcon,
        };
    const first = day.isToday ? undefined : day.lessons.find((l) => l.status !== 'cancelled');
    const held = day.lessons.filter((l) => l.status !== 'cancelled');
    const tests = held.filter((l) => l.isTest).map((l) => l.subject);
    if (first && day.date)
      next = {
        label:
          day.date === addDays(today, 1) ? t('studentHome.glass.now.tomorrow') : dayLong(day.date),
        lesson: first,
        extra: [
          t('studentHome.glass.now.lessons', {
            lessons: t('student.lessonsCount', { count: held.length }),
            from: timeOf(first.startsAt),
            to: timeOf((held.at(-1) as LessonItem).endsAt),
          }),
          tests.length > 0 && `${t('studentHome.glass.now.test')}: ${tests.join(', ')}`,
        ]
          .filter(Boolean)
          .join(' · '),
      };
    else nothing = t('studentHome.glass.now.noLessons');
  }
  return { ready: !day.pending, current, next, nothing, join };
}

// —— the latest grades ——

export const gradeTone = (g: StudentGrade): ToneName =>
  g.kind === 'thematic' || g.kind === 'semester' || g.kind === 'year'
    ? 'violet'
    : g.value
      ? (LEVEL_TONES[gradeLevel(g.value)] ?? 'grey')
      : 'grey';

/** The grades of the term, the newest first; «нова» — its notification not read yet. */
export function useGradeFeed(ctx: StudentContext) {
  const today = todayIso();
  const terms = useTerms(ctx.schoolId);
  const term = terms.data ? termFor(terms.data, today) : undefined;
  const record = useStudentRecord(ctx.child.memberId, term?.id);
  const notifications = useNotifications(useAllMemberIds());
  const unread = new Set(
    (notifications.data ?? [])
      .filter((n) => n.kind === 'grade' && !n.readAt && n.link.grade)
      .map((n) => n.link.grade),
  );
  const grades: StudentGrade[] = [...(record.data?.grades ?? [])]
    .filter((g) => g.value || g.special)
    .sort((a, b) => b.date.localeCompare(a.date));
  const fresh = grades.some((g) => unread.has(g.id) || g.date > addDays(today, -7));
  return { pending: record.isPending, grades, unread, fresh };
}

// —— what to do ——

export interface TaskRow {
  key: string;
  title: string;
  sub: string;
  /** the subject's colour, as a dot */
  subject?: string;
  link: TodoLink;
  /** the thing itself: «Свіжість» draws its row with the action and the due date in colour */
  item?: TodoItem;
}

export interface TaskGroup {
  key: string;
  tone: ToneName;
  art: (props: ArtProps) => ReactNode;
  title: string;
  count: number;
  rows: TaskRow[];
  link: TodoLink;
  /** a card with no rows: a line of words */
  text?: string;
}

export function useTaskGroups(ctx: StudentContext): { pending: boolean; groups: TaskGroup[] } {
  const { t } = useTranslation();
  const todo = useTodo(ctx);
  const today = todo.today;
  const of = (group: TodoGroup) => todo.groups.find((g) => g.group === group)?.items ?? [];
  const when = (iso: string) =>
    iso === today
      ? t('studentHome.todo.today')
      : iso === addDays(today, 1)
        ? t('studentHome.todo.tomorrow')
        : t(`student.on.${weekdayKey(iso)}`);
  const row = (i: TodoItem, due?: string): TaskRow => ({
    key: i.key,
    title: i.title,
    sub: [i.kind === 'quiz' ? t('studentHome.todo.quiz') : i.subject, due]
      .filter(Boolean)
      .join(' · '),
    subject: i.subject,
    link: i.link,
    item: i,
  });
  const groups: TaskGroup[] = [];
  const overdue = of('overdue').filter((i) => i.kind !== 'test');
  if (overdue.length)
    groups.push({
      key: 'overdue',
      tone: 'red',
      art: AlarmArt,
      title: t('studentHome.glass.board.overdue'),
      count: overdue.length,
      rows: overdue.map((i) =>
        row(i, i.due && t('studentHome.todo.overdueSince', { date: dayMonthShort(i.due) })),
      ),
      link: { to: '/homework' },
    });
  const ahead = [...of('today'), ...of('tomorrow'), ...of('week')];
  const soon = ahead.filter((i) => i.kind !== 'test');
  if (soon.length)
    groups.push({
      key: 'soon',
      tone: 'blue',
      art: PlaneArt,
      title: t('studentHome.glass.board.soon'),
      count: soon.length,
      rows: soon.map((i) => row(i, i.due && when(i.due))),
      link: { to: '/homework' },
    });
  const tests = ahead.filter((i) => i.kind === 'test');
  if (tests.length)
    groups.push({
      key: 'tests',
      tone: 'violet',
      art: ExamArt,
      title: t('studentHome.glass.board.tests'),
      count: tests.length,
      rows: tests.map((i) => ({
        key: i.key,
        title: i.title,
        sub: [
          i.due &&
            `${when(i.due)}${i.time ? ` ${t('studentHome.todo.at', { time: timeOf(i.time) })}` : ''}`,
          i.note,
        ]
          .filter(Boolean)
          .join(' · '),
        subject: i.title,
        link: i.link,
        item: i,
      })),
      link: { to: '/schedule' },
    });
  const rework = of('rework');
  if (rework.length)
    groups.push({
      key: 'rework',
      tone: 'orange',
      art: ReworkArt,
      title: t('studentHome.glass.board.rework'),
      count: rework.length,
      rows: rework.map((i) => ({
        ...row(i),
        sub: [i.subject, i.note && `«${i.note}»`].filter(Boolean).join(' · '),
      })),
      link: { to: '/homework' },
    });
  const ack = of('ack');
  if (ack.length)
    groups.push({
      key: 'ack',
      tone: 'pink',
      art: MegaphoneArt,
      title: t('studentHome.glass.board.ack'),
      count: ack.length,
      rows: ack.map((i) => ({ ...row(i), sub: t('studentHome.todo.announcement') })),
      link: { to: '/announcements' },
    });
  const catchUp = of('catchUp')[0];
  if (catchUp?.count)
    groups.push({
      key: 'catchUp',
      tone: 'green',
      art: FlagArt,
      title: t('studentHome.glass.board.catchUp'),
      count: catchUp.count,
      rows: [
        {
          key: 'catch-up',
          title: `${catchUp.count} ${t('studentHome.cards.catchUp.caption', { count: catchUp.count })}`,
          sub: (catchUp.subjects ?? []).join(', '),
          link: { to: '/catch-up' },
          item: catchUp,
        },
      ],
      link: { to: '/catch-up' },
    });
  if (!todo.pending && groups.length === 0)
    groups.push({
      key: 'done',
      tone: 'green',
      art: DoneArt,
      title: t('studentHome.glass.board.done'),
      count: 0,
      rows: [],
      link: { to: '/homework' },
      text: t('studentHome.glass.board.doneText'),
    });
  return { pending: todo.pending, groups };
}

// —— the school: fresh news for the students and the events of two weeks ——

export function useSchoolFeed(ctx: StudentContext) {
  const { t } = useTranslation();
  const { me } = useAuth();
  const today = todayIso();
  const memberIds = (me?.memberships ?? [])
    .filter((m) => m.school_id === ctx.schoolId)
    .map((m) => m.member_id);
  const announcements = useAnnouncements(ctx.schoolId, memberIds);
  const events = useEvents(ctx.schoolId, today);
  // only what is new and for the student; to confirm goes to «Треба зробити», the rest stays in its section
  const news = (announcements.data ?? [])
    .filter(
      (a) =>
        a.isRecipient &&
        !a.readAt &&
        !a.requiresAck &&
        a.publishedAt.slice(0, 10) >= addDays(today, -7),
    )
    .sort((a, b) => b.publishedAt.localeCompare(a.publishedAt))
    .slice(0, 2);
  const coming = (events.data ?? [])
    .filter(forStudent(ctx))
    .filter((e) => e.endsOn >= today && e.startsOn <= addDays(today, 14))
    .sort((a, b) => a.startsOn.localeCompare(b.startsOn))
    .slice(0, 4);
  /** «сьогодні», «вчора», «25 жовт.» */
  const ago = (iso: string) => {
    const d = iso.slice(0, 10);
    return d === today
      ? t('studentHome.glass.school.today')
      : d === addDays(today, -1)
        ? t('studentHome.glass.school.yesterday')
        : dayMonthShort(d);
  };
  /** «сьогодні», «завтра», «через 4 дні», «триває до 30 жовт.» */
  const until = (start: string, end: string) => {
    if (start < today) return t('studentHome.glass.school.goesOn', { date: dayMonthShort(end) });
    const days = Math.round((Date.parse(start) - Date.parse(today)) / 86_400_000);
    return days === 0
      ? t('studentHome.glass.school.today')
      : days === 1
        ? t('studentHome.glass.school.tomorrow')
        : t('studentHome.glass.school.in', { count: days });
  };
  return { news, coming, ago, until };
}
